import { Color, Spacing } from '@persona-id/design-tokens';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import ConditionalRow from 'dashboard/components/GraphQueryEditor/ConditionalRow';
import { ClusterQueryData, NHopsQueryData } from 'dashboard/components/GraphQueryEditor/form-state';
import NumHops from 'dashboard/components/GraphQueryEditor/NumHops';
import {
  ByClusterCreatedAtConditional,
  Conditional,
  LeftConstants,
  Operator,
  QueryConditionalConfig,
  QueryConfig,
  QueryType,
} from 'dashboard/models/graph-query';
import Button from 'design-system/components/Button';

const Well = styled.div`
  display: grid;
  grid-template-columns: 3.5% 1.5% 10% 5% 18% auto 2.5%;
  grid-gap: ${Spacing[2]};
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[4]};
`;

const AddButtonContainer = styled.div`
  grid-column: 1 / 8;
`;

const AddButton = styled(Button)`
  width: 50px;
  background-color: #d7d8df;
  -webkit-user-select: none; /* Safari */
  -ms-user-select: none; /* IE 10 and IE 11 */
  user-select: none; /* Standard syntax */
`;

const AddAccountConnectionConditionalButton = styled(Button)`
  width: 75px;
  background-color: #d7d8df;
  -webkit-user-select: none; /* Safari */
  -ms-user-select: none; /* IE 10 and IE 11 */
  user-select: none; /* Standard syntax */
`;

const Separator = styled.div`
  height: 1px;
  background-color: #d7d8df;
  grid-column: 1 / 8;
`;

interface NHopsQueryBodyProps {
  accountConnectionConditionalConfig: QueryConfig;
  loadingGraphEnvironment: boolean;
  nHopsQueryData: NHopsQueryData;
  properties: string[];
  queryConfig: QueryConfig;
  setClusterQueryData: (clusterQueryData: ClusterQueryData) => void;
  setNHopsQueryData: (nHopsQueryData: NHopsQueryData) => void;
  sharedPropertiesConfig: QueryConditionalConfig;
  showErrors: boolean;
}

const NHopsQueryBody = ({
  accountConnectionConditionalConfig,
  loadingGraphEnvironment,
  nHopsQueryData,
  properties,
  queryConfig,
  setClusterQueryData,
  setNHopsQueryData,
  sharedPropertiesConfig,
  showErrors,
}: NHopsQueryBodyProps) => {
  const conditionals = nHopsQueryData.startingSetConditionals;
  const setConditionals = (conditionals: Conditional[]) => {
    setNHopsQueryData({
      ...nHopsQueryData,
      startingSetConditionals: conditionals,
    });
  };
  const sharedPropertiesConditional = nHopsQueryData.sharedPropertiesConditional;
  const setSharedPropertiesConditional = (sharedPropertiesConditional: Conditional) => {
    setNHopsQueryData({
      ...nHopsQueryData,
      sharedPropertiesConditional,
    });
  };
  const traversalConditionals = nHopsQueryData.traversalConditionals || [];
  const setTraversalConditionals = (traversalConditionals: Conditional[]) => {
    setNHopsQueryData({
      ...nHopsQueryData,
      traversalConditionals,
    });
  };
  const numHops = nHopsQueryData.numberOfHops ?? 2;
  const setNumHops = (numberOfHops: number) => {
    setNHopsQueryData({
      ...nHopsQueryData,
      numberOfHops,
    });
  };

  const isPropertySearch = properties.includes(conditionals?.[0]?.left);

  const selectedLeftValues = new Set<string>(
    conditionals.map((conditional: Conditional) => conditional.left),
  );
  let permittedLeftConfigs: QueryConditionalConfig[] = [];
  if (isPropertySearch) {
    // Only property searches support multiple conditionals
    permittedLeftConfigs = queryConfig.filter(
      // properties that haven't been selected already
      (qc) => properties.includes(qc.left.key) && !selectedLeftValues.has(qc.left.key),
    );
  }

  const sharedPropertiesConfigWithAllOption = sharedPropertiesConfig
    ? {
        ...sharedPropertiesConfig,
        operatorToRight: {
          ...sharedPropertiesConfig.operatorToRight,
          [Operator.IncludesAnyOf]: {
            ...sharedPropertiesConfig.operatorToRight[Operator.IncludesAnyOf],
            allOption: { label: 'Any Property', value: '*' },
          },
        },
      }
    : undefined;

  const handleAddConditional = () => {
    if (permittedLeftConfigs.length === 0) {
      return;
    }
    setConditionals([
      ...conditionals,
      {
        left: permittedLeftConfigs[0].left.key,
        operator: Object.keys(permittedLeftConfigs[0].operatorToRight)[0] as Operator,
        right: [],
      } as Conditional,
    ]);
  };
  const handleChangeConditional = (conditionalIdx: number, newConditional: Conditional) => {
    if (conditionalIdx === 0 && newConditional.left === LeftConstants.ByClusterCreatedAt) {
      // The user changed from a n-hops query to a by-cluster query.
      setClusterQueryData({
        queryType: QueryType.ByCluster,
        createdAtConditional: newConditional as ByClusterCreatedAtConditional,
        sharedPropertiesConditional: nHopsQueryData.sharedPropertiesConditional,
        variables: [],
        minimumClusterSize: 2,
        numberOfHops: nHopsQueryData.numberOfHops,
      });
      return;
    }

    // We know that we're still doing an n-hops query.
    const newConditionalIsPropertySearch = properties.includes(newConditional.left);
    if (conditionalIdx === 0 && isPropertySearch && !newConditionalIsPropertySearch) {
      // If we change from a property search to a non-property search, clear all the other conditionals
      setConditionals([newConditional]);
    } else {
      const newConditionals = [...conditionals];
      newConditionals[conditionalIdx] = newConditional;
      setConditionals(newConditionals);
    }
  };
  const handleRemoveConditional = (conditionalIdx: number) => {
    setConditionals(conditionals.filter((_c, idx) => idx !== conditionalIdx));
  };

  const selectedTraversalLeftValues = new Set<string>(
    traversalConditionals.map((conditional: Conditional) => conditional.left),
  );

  const permittedTraversalLeftConfigs: QueryConditionalConfig[] =
    accountConnectionConditionalConfig.filter(
      (qc) => !selectedTraversalLeftValues.has(qc.left.key),
    );
  const handleAddTraversalConditional = () => {
    if (permittedTraversalLeftConfigs.length === 0) {
      return;
    }
    setTraversalConditionals([
      ...traversalConditionals,
      {
        left: permittedTraversalLeftConfigs[0].left.key,
        operator: Object.keys(permittedTraversalLeftConfigs[0].operatorToRight)[0] as Operator,
        right: [],
      } as Conditional,
    ]);
  };
  const handleChangeTraversalConditional = (
    conditionalIdx: number,
    newConditional: Conditional,
  ) => {
    const newConditionals = [...traversalConditionals];
    newConditionals[conditionalIdx] = newConditional;
    setTraversalConditionals(newConditionals);
  };

  const handleRemoveTraversalConditional = (conditionalIdx: number) => {
    setTraversalConditionals(traversalConditionals.filter((_c, idx) => idx !== conditionalIdx));
  };

  return (
    <>
      {!loadingGraphEnvironment && (
        <Well>
          {conditionals.map((conditional, idx) => {
            let possibleLeftConfigs: QueryConditionalConfig[];
            if (idx === 0) {
              // For the first conditional, allow any config not selected in another conditional already
              possibleLeftConfigs = queryConfig.filter(
                (qc) => conditional.left === qc.left.key || !selectedLeftValues.has(qc.left.key),
              );
            } else {
              // Only property searches allow multiple conditionals
              // In that case, for all other rows, allow properties that are selected by this row or that haven't been selected in another row
              possibleLeftConfigs = queryConfig.filter(
                (qc) =>
                  properties.includes(qc.left.key) &&
                  (conditional.left === qc.left.key || !selectedLeftValues.has(qc.left.key)),
              );
            }
            const canRemove = conditionals.length !== 1;
            return (
              <ConditionalRow
                key={conditional.left}
                conditional={conditional}
                configs={possibleLeftConfigs}
                label={idx === 0 ? 'Find' : 'And'}
                onChange={(c: Conditional) => handleChangeConditional(idx, c)}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                onRemove={canRemove ? () => handleRemoveConditional(idx) : null}
                renderLeft={true}
                showErrors={showErrors}
              />
            );
          })}
          {permittedLeftConfigs.length !== 0 && (
            <AddButtonContainer>
              <AddButton
                variant='basic'
                id='form-graph-query-account__add-row-button'
                onClick={handleAddConditional}
                type='button'
              >
                Add
              </AddButton>
            </AddButtonContainer>
          )}
          {sharedPropertiesConditional && (
            <>
              <Separator />
              <ConditionalRow
                connectedBy
                conditional={sharedPropertiesConditional}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                configs={[sharedPropertiesConfigWithAllOption]}
                label='Where Accounts connected by'
                onChange={setSharedPropertiesConditional}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                onRemove={null}
                renderLeft={false}
                showErrors={showErrors}
              />
              <>
                {traversalConditionals.map((conditional, idx) => {
                  const possibleTraversalLeftConfigs = accountConnectionConditionalConfig.filter(
                    (qc) =>
                      conditional.left === qc.left.key ||
                      !selectedTraversalLeftValues.has(qc.left.key),
                  );

                  return (
                    <ConditionalRow
                      key={conditional.left}
                      conditional={conditional}
                      configs={possibleTraversalLeftConfigs}
                      label={idx === 0 ? 'With' : 'And'}
                      onChange={(c: Conditional) => handleChangeTraversalConditional(idx, c)}
                      onRemove={() => handleRemoveTraversalConditional(idx)}
                      renderLeft={true}
                      showErrors={showErrors}
                    />
                  );
                })}
                {permittedTraversalLeftConfigs.length > 0 && (
                  <AddButtonContainer>
                    <AddAccountConnectionConditionalButton
                      variant='basic'
                      id='form-graph-query-account-connections__add-row-button'
                      onClick={() => {
                        handleAddTraversalConditional();
                      }}
                      type='button'
                    >
                      Add Filter
                    </AddAccountConnectionConditionalButton>
                  </AddButtonContainer>
                )}
              </>
            </>
          )}
          <NumHops
            numHops={numHops}
            onChange={(nums: number) => {
              setNumHops(nums);
            }}
          />
        </Well>
      )}
    </>
  );
};

export default NHopsQueryBody;
